import { useCallback, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Level } from './types';

// Persists the table's column order and visibility to localStorage, per level
// (the ad and asset tables have entirely different columns). Follows the same
// shape as AdLibrary's useColumnPreferences: store ids, sanitize on read, and
// fall back to the code-defined order whenever the stored layout no longer
// matches the columns that exist.

const STORAGE_KEY = 'sbly.creativePerformance.columns.v1';

// The identity column is what every row is read from, so it is never hidden or
// moved out of first position.
export const LOCKED_FIELD = 'group_key';

interface LevelLayout {
  order: string[];
  hidden: string[];
  pinned?: string[];
}

type PersistedState = Partial<Record<Level, LevelLayout>>;

function loadState(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as PersistedState;
  } catch {
    // A corrupt entry should cost the user their column layout, not the page.
    return {};
  }
}

function saveState(state: PersistedState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private browsing / quota. Not worth breaking the table over.
  }
}

export default function useColumnLayout(
  level: Level,
  columns: GridColDef[],
  defaultHidden: string[] = [],
) {
  const [state, setState] = useState<PersistedState>(loadState);

  const layout = state[level];

  // Stored ids are filtered against the columns that actually exist, so a
  // renamed or attribution-hidden column can't strand the layout.
  const orderedFields = useMemo(() => {
    const available = columns.map(column => column.field);
    const availableSet = new Set(available);
    const stored = (layout?.order || []).filter(field => availableSet.has(field));
    const missing = available.filter(field => !stored.includes(field));
    const merged = [...stored, ...missing].filter(field => field !== LOCKED_FIELD);
    return [LOCKED_FIELD, ...merged].filter(field => availableSet.has(field));
  }, [columns, layout]);

  // Until the user has expressed a preference for this level, fall back to the
  // caller's default-hidden set. Once they have, their choice wins outright —
  // including the choice to hide nothing.
  // Pinning used to live in the per-column menu, which is gone; it lives in the
  // Columns dialog now. The identity column is always pinned — it's what every
  // row is read from, so it has to stay in view when scrolling sideways.
  const pinnedFields = useMemo(
    () => new Set([LOCKED_FIELD, ...(layout?.pinned || [])]),
    [layout],
  );

  const pinnedColumns = useMemo(
    () => ({ left: orderedFields.filter(field => pinnedFields.has(field)) }),
    [orderedFields, pinnedFields],
  );

  // The stored preference, kept whole. A column the current grouping doesn't
  // render still has a remembered show/hide state, and pruning it here would
  // silently forget it the next time anything else was toggled.
  //
  // A column added to the code after this layout was saved is not in its order,
  // which means the user has never seen it — so they cannot have chosen to show
  // it, and the code default still applies. Without this every new
  // default-hidden column arrives switched ON for everyone who has ever touched
  // the Columns dialog, and only stays off for people who never did.
  const storedHidden = useMemo(() => {
    if (!layout) {
      return new Set(defaultHidden.filter(field => field !== LOCKED_FIELD));
    }
    const seen = new Set(layout.order || []);
    const unseen = defaultHidden.filter(field => !seen.has(field));
    return new Set(
      [...(layout.hidden || []), ...unseen].filter(field => field !== LOCKED_FIELD),
    );
  }, [layout, defaultHidden]);

  // What is actually hidden right now — intersected with the columns that exist
  // at this level. Without this the "N hidden" badge counts fields the table
  // has never heard of: the ad-level defaults name seven columns, of which the
  // asset table has two.
  const hiddenFields = useMemo(() => {
    const available = new Set(columns.map(column => column.field));
    return new Set(Array.from(storedHidden).filter(field => available.has(field)));
  }, [columns, storedHidden]);

  const visibleColumns = useMemo(() => {
    const byField = new Map(columns.map(column => [column.field, column]));
    return orderedFields
      .filter(field => !hiddenFields.has(field))
      .map(field => byField.get(field))
      .filter((column): column is GridColDef => !!column);
  }, [columns, orderedFields, hiddenFields]);

  const update = useCallback(
    (next: LevelLayout) => {
      setState(current => {
        const merged = { ...current, [level]: next };
        saveState(merged);
        return merged;
      });
    },
    [level],
  );

  const current = useCallback(
    (overrides: Partial<LevelLayout>): LevelLayout => ({
      order: orderedFields,
      // Persist the whole stored set, not just what's visible at this grouping.
      hidden: Array.from(storedHidden),
      pinned: Array.from(pinnedFields).filter(field => field !== LOCKED_FIELD),
      ...overrides,
    }),
    [orderedFields, storedHidden, pinnedFields],
  );

  const setOrder = useCallback(
    (order: string[]) => update(current({ order })),
    [update, current],
  );

  const togglePin = useCallback(
    (field: string) => {
      if (field === LOCKED_FIELD) return;
      const pinned = new Set(pinnedFields);
      if (pinned.has(field)) pinned.delete(field);
      else pinned.add(field);
      update(
        current({ pinned: Array.from(pinned).filter(item => item !== LOCKED_FIELD) }),
      );
    },
    [update, current, pinnedFields],
  );

  const toggleField = useCallback(
    (field: string) => {
      if (field === LOCKED_FIELD) return;
      const hidden = new Set(storedHidden);
      if (hidden.has(field)) hidden.delete(field);
      else hidden.add(field);
      update(current({ hidden: Array.from(hidden) }));
    },
    [update, current, storedHidden],
  );

  // Indices are into the movable portion of the list — the locked identity
  // column sits outside it and can never be displaced.
  const reorderFields = useCallback(
    (from: number, to: number) => {
      const movable = orderedFields.filter(field => field !== LOCKED_FIELD);
      if (from < 0 || to < 0 || from >= movable.length || to >= movable.length) {
        return;
      }
      movable.splice(to, 0, movable.splice(from, 1)[0]);
      update(current({ order: [LOCKED_FIELD, ...movable] }));
    },
    [update, current, orderedFields],
  );

  const reset = useCallback(() => {
    setState(current => {
      const merged = { ...current };
      delete merged[level];
      saveState(merged);
      return merged;
    });
  }, [level]);

  return {
    visibleColumns,
    orderedFields,
    hiddenFields,
    pinnedFields,
    pinnedColumns,
    toggleField,
    togglePin,
    reorderFields,
    setOrder,
    reset,
    hiddenCount: hiddenFields.size,
  };
}
